import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { ContactTypeValue } from '../../common/enums/contact-type.value.enum';
import { CurrencyTransformPipe } from '../../common/pipes/currency-format.pipe';
import { GroupByPipe } from '../../common/pipes/group-by.pipe';
import { DelivaryOptionType, MemberType } from '../../customerservice/common/constants';
import { DeliveryPreference, MemberLevel, OrderType, PurchaseFlow } from '../../customerservice/common/enumerations';
import { PriceTier, StorerPiceTier } from '../../customerservice/constants/pricetier.constants';
import { DisplaySectionId, DocumentLibraryMemberId, Portal } from '../../customerservice/enums/document-library-member-id.enum';
import { ImageType } from '../../customerservice/enums/image-type.enum';
import { MemberTypeEnum } from '../../customerservice/enums/member-type.enum';
import { AddressType, PaymentMethodTypes, PaymentType, PriceType, ProductWeightMetric, PurchaseFlows, ShippingType } from '../../customerservice/enums/order-source-type';
import { OrderSourceValue } from '../../customerservice/enums/order-source.enum';
import { ProductMetatDataValue } from '../../customerservice/enums/product-metadata.enum';
import { SelectEnum } from '../../customerservice/enums/selection.enum';
import { Address } from '../../customerservice/interface/address';
import { AppCountryConfig } from '../../customerservice/interface/app-config';
import { CheckoutConfig } from '../../customerservice/interface/checkout-config.interface';
import { DocumentLibrary, DocumentLibraryRequest } from '../../customerservice/interface/enrollment/document-library.interface';
import { EnrollInfo } from '../../customerservice/interface/enrollment/enroll-info.interface';
import { Order } from '../../customerservice/interface/enrollment/order.interface';
import { OrderRulesResponse } from '../../customerservice/interface/order-rules-response.interface';
import { Product } from '../../customerservice/interface/product-info';
import { ProductModel, ProductRestrictionModel } from '../../customerservice/interface/product-restriction.interface';
import { CollectionPoint } from '../../customerservice/interface/pudo-location.interface';
import { ShippingProductModel } from '../../customerservice/interface/shipping-product.interface';
import { VerifyAccountModel } from '../../customerservice/interface/verify-account-exits-model';
import { AppConfigService } from '../../customerservice/services/app-config.service';
import { SSO } from '../../sso/sso.constants';
import { Language } from '../components/ILanguage';
import { Country } from '../interface/country.interface';
import { DisplayPriceConfig } from '../interface/display-price-config.interface';
import { StoreConfig } from '../interface/StoreConfig.interface';
import { CommonService } from './common.service';

@Injectable()
export class EnrollmentCommonService {

    momentConstructor: (value?: any) => moment.Moment = (<any>moment).default || moment;
    productDiscountPercentage: string;
    priceTierName:string;
    appConfig: AppCountryConfig;
    constructor(
        private _groupByPipe: GroupByPipe,
        private _currencyPipe: CurrencyTransformPipe,private _appConfigService: AppConfigService,
        private _commonService: CommonService,
        ) {}
    getMemberTitleId(typeOfOrder: OrderType) {
        return typeOfOrder === OrderType.NEW_FBO ? MemberLevel.ASSISTANTSUPERVISOR : MemberLevel.PREFERREDCUSTOMER;
    }

    getMemberType(typeOfOrder: OrderType) {
        return typeOfOrder === OrderType.NEW_FBO ? MemberType.Distributor : MemberType.PREFERRED_CUSTOMER;
    }

    getMemberTypeId(typeOfOrder: OrderType) {
        return typeOfOrder === OrderType.NEW_FBO ? MemberTypeEnum.DISTRIBUTOR : MemberTypeEnum.FPC;
    }

    getPurchaseFlow(typeOfOrder: OrderType) {
        return typeOfOrder === OrderType.NEW_FBO || typeOfOrder === OrderType.NEW_FPC ? PurchaseFlows.ENROLLMENT : PurchaseFlows.PERSONAL;
    }

    getOrderTypeId(typeOfOrder: OrderType) {
        return typeOfOrder === OrderType.NEW_FBO || typeOfOrder === OrderType.NEW_FPC ? PurchaseFlow.Enrollment : PurchaseFlow.PERSONAL;
    }


    getAdyenOrderTypeId(typeOfOrder: OrderType): number {
        return typeOfOrder === OrderType.NEW_RETAIL || typeOfOrder === OrderType.NEW_GUEST_RETAIL ? PurchaseFlow.Retail: PurchaseFlow.Personal_Order;
    }
    buildForm(formBuilder: FormBuilder, typeOfOrder: OrderType, items: any) {
        return formBuilder.group({
            orderType: typeOfOrder,
            items: formBuilder.array(items),
            countryId: ['', [Validators.required]],
            participatingCountry: [''],
            fboEligibleProducts: [''],
            productOptions: [''],
            deliveryPreference: [DeliveryPreference.DELIVERY],
            shippingAddressId: [''],
            shipMethodId: ['', [Validators.required]],
            originalShippingCost: [0, [Validators.required]],
            shippingCost: [''],
            shippingCostPercentage: [''],
            itemSearch: [''],
            itemLevelSearch: [''],
            shippingCostSelector: [SelectEnum.firstOption.toString()],
            paymentInstrumentId: ['', [Validators.required]],
            paymentSelector: [],
            backDateOrder: [false],
            saveForFutureUse: [false],
            language: [],
            isContactSponsor: [null],
            isShippingDiscountApplied: [false],
            originalShippingTax: [0, [Validators.required]]
        });
    }

    createItemNumberGroup(formBuilder: FormBuilder) {
        return formBuilder.group({
            itemNumber: [''],
            quantity: [''],
            productId: [''],
            productItem: [{}],
            productNumber: [''],
            productName: [''],
            productImage: [[]],
            disableIncrement: [false],
            disableDelete: [false],
            disableDecrement: [true],
            isDepositFeeProduct: [false],
            depositFeeEnabled:[false]
        });
    }

    getPriceTypeId(): PriceType {
        return PriceType.WHOLESALEPRICE;
    }

    getWarehouseAddress(storeInfo, selectedCountryCode): Address {
        let warehouseAddress: Address = null;
        if (storeInfo && storeInfo.warehouseAddress) {
            warehouseAddress = {
                addressLine1: storeInfo.warehouseAddress.addressLine1,
                addressLine2: storeInfo.warehouseAddress.addressLine2,
                addressLine3: storeInfo.warehouseAddress.addressLine3,
                addressLine4: storeInfo.warehouseAddress.addressLine4,
                city: storeInfo.warehouseAddress.city,
                state: storeInfo.warehouseAddress.stateCode,
                stateCode: storeInfo.warehouseAddress.stateCode,
                countryCode: storeInfo.warehouseAddress.countryCode || selectedCountryCode,
                country: storeInfo.warehouseAddress.countryCode || selectedCountryCode,
                postalCode: storeInfo.warehouseAddress.zipCode,
                zip: storeInfo.warehouseAddress.zipCode,
                countryId: storeInfo.warehouseAddress.countryId,
                stateId: storeInfo.warehouseAddress.stateOrProvinceId,
                name: storeInfo.warehouseAddress.name ? storeInfo.warehouseAddress.name:null
            };
        }
        return warehouseAddress;
    }

    getShippingAddress(shippingAddress,pudoPickupPersonAddress, selectedCountryCodeTwo, formValue, countries, foreverDirectEnabled?:boolean): any {
        let shippingAddressInfo = null;
        if (formValue.deliveryPreference === DeliveryPreference.DROPSHIP) {
            shippingAddressInfo = pudoPickupPersonAddress;

        } else {
            shippingAddressInfo = shippingAddress;
        }
        if (shippingAddressInfo) {
            const countryObj = this.getCountryDetailsByCountryIdOrCode(countries, null, shippingAddressInfo.countryCode);
            const shippingAddress: any = {
                addressLine1: shippingAddressInfo.addressLine1,
                addressLine2: shippingAddressInfo.addressLine2,
                addressLine3: shippingAddressInfo.addressLine3,
                addressLine4: shippingAddressInfo.addressLine4,
                addressVerified: shippingAddressInfo.addressVerified,
                latitude: shippingAddressInfo.latitude,
                longitude: shippingAddressInfo.longitude,
                city: shippingAddressInfo.city || null,
                state: shippingAddressInfo.stateName,
                stateCode: shippingAddressInfo.stateCode,
                countryCode: shippingAddressInfo.countryCode,
                country: shippingAddressInfo.countryName ? shippingAddressInfo.countryName : countryObj && countryObj.name ? countryObj.name : '',
                postalCode: shippingAddressInfo.postalCode ? shippingAddressInfo.postalCode.toUpperCase() : null,
                zip: shippingAddressInfo.postalCode ? shippingAddressInfo.postalCode.toUpperCase() : null,
                isShowStateCode : shippingAddressInfo.isShowStateCode,
                countryId: shippingAddressInfo.countryId,
                stateId: shippingAddressInfo.stateId,
                name: shippingAddressInfo.shipToName,
                firstName: shippingAddressInfo.firstName,
                middleName: shippingAddressInfo.middleName,
                lastName: shippingAddressInfo.lastName,
                addressType: formValue.deliveryPreference === DeliveryPreference.DROPSHIP?AddressType.PICKUP_PERSON:AddressType.SHIPPING,
                phone: shippingAddressInfo.phone ? this.getValidPhoneNumber(shippingAddressInfo.phone,foreverDirectEnabled) : null,
                phoneNumber: shippingAddressInfo.phoneNumber ? this.getValidPhoneNumber(shippingAddressInfo.phoneNumber,foreverDirectEnabled)  : null,
                countryCodeTwo: selectedCountryCodeTwo,
                phoneType: shippingAddressInfo.phoneType,
                saveForFutureUse: formValue.saveForFutureUse,
                deliveryInstruction: shippingAddressInfo.deliveryInstruction,
                email: shippingAddressInfo.email ? shippingAddressInfo.email : null
            };
            return shippingAddress;
        }
        return null;
    }

    getValidPhoneNumber(phoneNumber: string,foreverDirectEnabled:boolean){
        if(phoneNumber && foreverDirectEnabled){
            phoneNumber = phoneNumber.replace(/[a-zA-Z ]/g, '');
            if(phoneNumber.substring(0,1) !=='+' && phoneNumber.substring(0,2) !== '00'){
                phoneNumber = '+' + phoneNumber;
            }
        }
        else{
            phoneNumber = phoneNumber.replace(/[^a-zA-Z0-9]/g, '');
        } 
        return phoneNumber;
    }

    getCountryTaxInclusiveInfo(country: Country): boolean {
        let countryTaxInclusive = false;
        if (country && country.countryMetadata &&
            country.countryMetadata.length > 0 &&
            country.countryMetadata[0].metadata) {
            countryTaxInclusive = (country.countryMetadata[0].metadata[0].value === 'TRUE') ? true : false;
        }
        return countryTaxInclusive;
    }

    getCountryDetailsByCountryIdOrCode(countries: any, countryId: number, countryCode: string): any {
        let countryObj = {};
        if (countryId) {
            countryObj = countries.filter((x: any) => x.countryId === countryId)[0];
        }
        if (countryCode) {
            countryObj = countries.filter((x: any) => x.isocodeThree === countryCode)[0];
        }
        return countryObj;
    }

    getStateDetailsByStateIdOrCode(states: any, stateId: number, stateCode: string, countryCode?: string): any {
        let stateObj = {};
        if (stateId) {
            stateObj = states.filter((x: any) => x.id === stateId)[0];
        }
        if (stateCode && countryCode) {
            stateObj = states.filter((x: any) => x.stateCode === stateCode && x.countryCode === countryCode)[0];
        } else if (stateCode){
            stateObj = states.filter((x: any) => x.stateCode === stateCode)[0];
        }
        return stateObj;
    }

    getShippingMethodWarehouseAddress(shipMethod: any): Address {
        if (shipMethod && shipMethod.warehouseAddress) {
            shipMethod.warehouseAddress.postalCode = shipMethod.warehouseAddress.zip;
            shipMethod.warehouseAddress.country = shipMethod.warehouseAddress.countryCode;
            return shipMethod.warehouseAddress;
        }
    }

    getPickUpAddress(shipMethod: any, deliveryPreference: DeliveryPreference, countries: any, states: any, countryCode: string): any {
        if (shipMethod && shipMethod.pickUpLocation
            && deliveryPreference === DeliveryPreference.PICKUP) {
            shipMethod.pickUpLocation.addressType = AddressType.PICKUP;

            const countryObj = this.getCountryDetailsByCountryIdOrCode(countries, null, countryCode);
            const stateObj = this.getStateDetailsByStateIdOrCode(states, null, shipMethod.pickUpLocation.stateCode);

            if (countryObj) {
                shipMethod.pickUpLocation.countryId = countryObj.countryId;
                shipMethod.pickUpLocation.country = countryObj.isocodeThree ? countryObj.isocodeThree : '';
                shipMethod.pickUpLocation.countryCodeTwo = countryObj.isocodeTwo ? countryObj.isocodeTwo : '';
                shipMethod.pickUpLocation.countryCode = countryObj.isocodeThree ? countryObj.isocodeThree : '';
            }
            if (stateObj) {
                shipMethod.pickUpLocation.stateId = stateObj.id;
            }
            shipMethod.pickUpLocation.name = shipMethod.pickUpLocation.pickupCenterName;
            shipMethod.pickUpLocation.postalCode = shipMethod.pickUpLocation.zip;
            return shipMethod.pickUpLocation;
        } else {
            return null;
        }
    }

    calculateSubTotal(store: StoreConfig, taxInclusiveShippingCountry: boolean, item: any, checkoutConfig: CheckoutConfig, appconfig?:AppCountryConfig,iscalculateTax?:boolean): number {
        let subTotal = 0;
        if (item) {
            const isCalculateTaxExclusivePrice = checkoutConfig && checkoutConfig.calculateTaxExclusivePrice;
            subTotal += this.calculateItemSubtotal(item, store, taxInclusiveShippingCountry,
                isCalculateTaxExclusivePrice,appconfig,iscalculateTax);
        }
        if (store) {
            subTotal = Number(subTotal.toFixed(Math.max(0, ~~store.numberDecimals)));
        }
        return subTotal;
    }

    calculateSubtotalForVatRegisteredUser(item: any, store: StoreConfig, taxInclusiveShippingCountry: boolean,
        isVATRegistered: boolean, taxCalculated: boolean) {
        if (taxCalculated && isVATRegistered) {
            return Number(item.catalogPrice.
                // tslint:disable-next-line:no-bitwise
                toFixed(Math.max(0, ~~(store && store.numberDecimals ? store.numberDecimals : 2)))) * item.quantity;
        } else {
            return this.calculateItemSubtotal(item, store, taxInclusiveShippingCountry);
        }

    }
    calculateItemSubtotal(item: any, store: StoreConfig, taxInclusiveShippingCountry: boolean, isCalcTaxExclusivePrice?: boolean,appconfig?:AppCountryConfig,isCalculateTax?:boolean): number {
        this.appConfig = appconfig;
        if(appconfig && appconfig.enableVatItemizationForNonVat){
                return Number(item[isCalculateTax?'catalogPrice':'applicablePrice'].
        // tslint:disable-next-line:no-bitwise
        toFixed(Math.max(0, ~~(store && store.numberDecimals ? store.numberDecimals : 2)))) * item.quantity;
       }else{
        return Number(item[(taxInclusiveShippingCountry === false || isCalcTaxExclusivePrice) ? 'catalogPrice' : 'applicablePrice'].
            toFixed(Math.max(0, ~~(store && store.numberDecimals ? store.numberDecimals : 2)))) * item.quantity;
       }
    }

    getPriceTier(products: any[]) {
        let priceTierName = '';
        if (products && products.length > 0) {
            priceTierName = products[0].priceTierName;
        }
        return priceTierName;
    }

    getTaxExclusiveShippingCost(shippingCostValue: any, isShippingDiscountApplied: any, orderFormGroup: any,
            shippingCostWaived: boolean): number {
        let shippingCost = shippingCostValue || 0;
        if (shippingCostWaived) {
            shippingCost = 0;
        } else if (isShippingDiscountApplied) {
            if (orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.thirdOption.toString()
                && orderFormGroup.controls['shippingCostPercentage'].value === 100) {
                shippingCost = 0;
            }
        }
        return shippingCost;
    }

    mapItemstoTaxItems(mapCartToTaxCart: any, isFPC?: boolean): any[] {
        const taxItems: any[] = [];
        if (mapCartToTaxCart.products && mapCartToTaxCart.products.length > 0) {
            let itemLineNumber = 0;
            mapCartToTaxCart.products.forEach((x: any) => {
                let taxablePrice: any;
                let paidPrice: any;
                if (mapCartToTaxCart.checkoutConfig && mapCartToTaxCart.checkoutConfig.calculateTaxExclusivePrice) {
                    taxablePrice = x.catalogPrice;
                    paidPrice = x.applicablePrice;
                } else {
                    taxablePrice = ((mapCartToTaxCart.checkoutConfig.catalogPriceAsTaxablePrice ||
                        (mapCartToTaxCart.address && mapCartToTaxCart.address.stateCode
                            && mapCartToTaxCart.address.stateCode === 'PR')) ? x.catalogPrice : x.retailPrice);
                    paidPrice = ((mapCartToTaxCart.checkoutConfig.applicablePriceAspaidPrice ||
                        (mapCartToTaxCart.address && mapCartToTaxCart.address.stateCode
                            && mapCartToTaxCart.address.stateCode === 'PR')) ? x.applicablePrice : x.retailPrice);
                }
                const taxItem: any = {
                    lineNumber: ++itemLineNumber,
                    itemNumber: x.itemNumber,
                    taxablePrice: this.getTaxablePrice(mapCartToTaxCart,x,isFPC,taxablePrice),
                    quantity: +x.quantity,
                    taxCode: null,
                    description: '',//x.shortDescription,
                    taxInclusive: mapCartToTaxCart.countryTaxInclusive ? true : false,
                    resale: false,
                    resalePrice: 0,
                    taxBreakDown: x.taxBreakDown,
                    productIdentifier: null,
                    retailPrice: x.retailPrice,
                    paidPrice: this.getPaidPrice(mapCartToTaxCart,x,isFPC,paidPrice),
                    wholeSalePrice: x.wholesalePrice,
                    salesTax: 0,
                    revisionNumber: x.revisionNumber,
                    itemWeight: x.shipping && x.shipping.weight ? x.shipping.weight : 0,
                    importedMeasurementUnit: x.importedMeasurementUnit,
                    unitTaxExclusiveRetailPrice: x.baseRetailPrice,
                    computedTaxInclusivePrice: x.computedTaxInclusivePrice,
                    comboBOMEnabled: x.display ? (x.display.comboBOMEnabled ? x.display.comboBOMEnabled : false) : false,
                    computedComboTaxTotals: x.computedComboTaxTotals && x.computedComboTaxTotals.length > 0 ? x.computedComboTaxTotals : []

                };
                taxItems.push(taxItem);
            });
        }
        return taxItems;
    }


    getTaxablePrice(mapCartToTaxCart: any, product: any, isFPC: boolean, taxablePrice: number): number {
        if (mapCartToTaxCart.checkoutConfig && mapCartToTaxCart.checkoutConfig.applicablePriceAsTaxablePrice && (product.priceTierName === PriceTier.NEWDST || product.priceTierName === PriceTier.WHLSAL)) {
            return product.applicablePrice;
        } else {
            return taxablePrice;
        }
    }
    getPaidPrice(mapCartToTaxCart: any, product: any, isFPC: boolean, paidPrice: number): number {
        if (mapCartToTaxCart.checkoutConfig && mapCartToTaxCart.checkoutConfig.applicablePriceAsTaxablePrice && (product.priceTierName === PriceTier.NEWDST || product.priceTierName === PriceTier.WHLSAL)) {
            return product.applicablePrice;
        } else {
            return paidPrice;
        }
    }
    
    getProductModels(products: any[]): ShippingProductModel[] {
        const productModels: ShippingProductModel[] = [];
        if (products && products.length > 0) {

            for (let index = 0; index < products.length; index++) {

                const product = products[index];
                if (product) {
                    const productModel: ShippingProductModel = {
                        productID: product.id,
                        productHeight: product.shipping ? product.shipping.height : 0,
                        productWeight: product.shipping ? product.shipping.weight : 0,
                        productQuantity: product.quantity,
                        productLength: product.shipping ? product.shipping.length : 0,
                        productWidth: product.shipping ? product.shipping.width : 0,
                        wholesalePrice: product.wholesalePrice,
                        sellingPrice: product.applicablePrice,
                        isOrderLiteratureOnly: product.literature,
                        retailPrice: product.retailPrice,
                        weightMetricId: ProductWeightMetric.KGS,
                        isShippable: product.shipping ? product.shipping.shippingRequired : 0,
                        itemNumber: product.itemNumber
                    };
                    productModels.push(productModel);
                }

            }
        }
        return productModels;
    }

    prepareProductRestrictionModelRequest(pickupAddress: any, products: any): ProductRestrictionModel {
        let productRestrictionModel: ProductRestrictionModel;
        productRestrictionModel = {
            countryCode: pickupAddress.countryCode,
            stateCode: pickupAddress.stateCode
        };
        const productModel: ProductModel[] = [];
        if (products && products.length > 0) {
            products.forEach(item => {
                productModel.push({ itemNumber: item.itemNumber });
            });
        }
        productRestrictionModel.productModels = productModel;
        return productRestrictionModel;
    }

    /**
       * verified Address Mapping
       * @param  {any}  address
       * @returns any
       */
      mapVerifiedAddressToFormFields(address: any,requestData:any) {
        const countryObj: any = requestData.countryObj;
        const stateObj: any = requestData.stateObj;
        if (address) {
            const mapAddressToFields = {
                name: address.name,
                firstName: address.firstName,
                middleName: address.middleName,
                lastName: address.lastName,
                addressLine1: address.addressLine1,
                addressLine2: address.addressLine2,
                addressLine3: address.addressLine3 ? address.addressLine3 : '',
                addressLine4: address.addressLine4 ? address.addressLine4 : '',
                postalCode: address.postalCode,
                email: address.email,
                city: address.city,
                stateId: stateObj.id,
                stateCode: stateObj.stateCode,
                countryCode: countryObj.countryCode || countryObj.isocodeThree,
                countryId: countryObj.countryId,
                phone: address.phone ? address.phone.replace(/[^a-zA-Z0-9]/g, '') : null,
                phoneNumber: address.phone ? address.phone : null,
                addressVerified: requestData.addressVerified,
                latitude: address.latitude,
                longitude: address.longitude,
                phoneType: address.phoneType,
                saveForFutureUse: requestData.saveForFutureUse,
                disableAddressBlock: address.disableAddressBlock,
                deliveryInstruction: address.deliveryInstruction
            };
            return mapAddressToFields;
        }
    }

    prepareRequestToSaveAddress(memberInfo, addressDetails: any, saveForFutureUse){
        const saveAddressRequest: any = {
            id: null,
            memberId: memberInfo ? memberInfo.memberId : null,
            addressLine1: addressDetails.addressLine1,
            addressLine2: addressDetails.addressLine2,
            addressLine3: addressDetails.addressLine3 ? addressDetails.addressLine3 : '',
            addressLine4: addressDetails.addressLine4 ? addressDetails.addressLine4 : '',
            shipToName: addressDetails.name,
            city: addressDetails.city,
            stateId: addressDetails.stateId,
            countryId: addressDetails.countryId,
            postalCode: addressDetails.postalCode || addressDetails.zip,
            contactTypeID: 1,
            defaultAddress: false,
            addressVerified: addressDetails.addressVerified,
            latitude: addressDetails.latitude,
            longitude: addressDetails.longitude,
            addressTypeId: 40,
            loggedInUserId: memberInfo.userId,
            responseCodes: addressDetails.responseCodes ? addressDetails.responseCodes : null,
            phone: addressDetails.phone,
            phoneNumber: addressDetails.phone,
            firstName: addressDetails.firstName,
            middleName: addressDetails.middleName,
            lastName: addressDetails.lastName,
            phoneType: addressDetails.phoneType,
            saveForFutureUse: saveForFutureUse,
            disableAddressBlock: addressDetails.disableAddressBlock,
            deliveryInstruction: addressDetails.deliveryInstruction
        };
        return saveAddressRequest;
    }

    mapNewShippingAddress(addressInfo: any, stateObj: any, countryObj: any, saveForFutureUse: boolean){
        const mappedAddress = {
            shipToName: addressInfo.name,
            firstName: addressInfo.firstName,
            middleName: addressInfo.middleName,
            lastName: addressInfo.lastName,
            addressLine1: addressInfo.addressLine1,
            addressLine2: addressInfo.addressLine2,
            addressLine3: addressInfo.addressLine3,
            addressLine4: addressInfo.addressLine4,
            email: addressInfo.email,
            city: addressInfo.city,
            stateId: stateObj.id,
            stateName: stateObj.stateName || '',
            state: stateObj.stateCode || '',
            stateCode: stateObj.stateCode || '',
            countryId: countryObj.countryId,
            countryName: countryObj.name || '',
            country: countryObj.isocodeThree || '',
            countryCode: countryObj.isocodeThree || '',
            postalCode: addressInfo.postalCode || addressInfo.zip,
            zip: addressInfo.postalCode || addressInfo.zip,
            phone: addressInfo.phone,
            phoneNumber: addressInfo.phone,
            addressVerified: addressInfo.addressVerified,
            latitude: addressInfo.latitude,
            longitude: addressInfo.longitude,
            phoneType: addressInfo.phoneType,
            isShowStateCode: addressInfo.isShowStateCode,
            saveForFutureUse: saveForFutureUse,
            disableAddressBlock: addressInfo.disableAddressBlock,
            deliveryInstruction: addressInfo.deliveryInstruction
        };
        return mappedAddress;
    }
    /**
     * @description get Terms and Conditions Document
     */
    validateAgreementSelection(agreementDocuments: DocumentLibrary[]): boolean {
        let agreementCheckError: boolean;
        const unselectedAgreements = agreementDocuments.filter(agreement => !agreement.isSelected);
        if (unselectedAgreements && unselectedAgreements.length > 0) {
            agreementCheckError = true;
        } else {
            agreementCheckError = false;
        }
        return agreementCheckError;
    }

    calculateItemsSubTotal(store: StoreConfig, taxInclusiveShippingCountry: boolean, products: any[],
        checkoutConfig: CheckoutConfig, isVATRegistered: boolean, isEnableVATCalculation: boolean, appConfig?:AppCountryConfig,isCalculateTax?:boolean): number {
        let itemsSubTotal = 0;
        const isCalculateTaxExclusivePrice = checkoutConfig && checkoutConfig.calculateTaxExclusivePrice;
        for (let i = 0; i < products.length; i++) {
            if (isVATRegistered && store.taxInclusive && isEnableVATCalculation) {
                itemsSubTotal += this.calculateSubtotalForVatRegisteredUser(products[i],
                    store, taxInclusiveShippingCountry, isVATRegistered, true);
            } else {
                const iscalculatetax=isVATRegistered || isCalculateTax ?true:false;
                itemsSubTotal += this.calculateItemSubtotal(products[i], store,
                taxInclusiveShippingCountry, isCalculateTaxExclusivePrice,appConfig,iscalculatetax);
            }
        }

        if (store) {
            // tslint:disable-next-line:no-bitwise
            itemsSubTotal = Number(itemsSubTotal.toFixed(Math.max(0, ~~store.numberDecimals)));
        }
        return itemsSubTotal;
    }

    calculateDiscountAmount(item: any, displayPriceConfig: DisplayPriceConfig): number {
        let discountAmount = 0;
        if (item && displayPriceConfig && displayPriceConfig.displayRetailPrice) {
            discountAmount = (parseFloat(item.retailPrice.toFixed(2)) * item.quantity) -
                (parseFloat(item.applicablePrice.toFixed(2)) * item.quantity);
        } else {
            discountAmount = (parseFloat(item.baseRetailPrice.toFixed(2)) * item.quantity) -
                (parseFloat(item.catalogPrice.toFixed(2)) * item.quantity);
        }
        return discountAmount;
    }

    calculateNetUnitPrice(product: any): number {
        const netUnitPrice = product.computedTaxInclusivePrice / (1 + (product.taxRate / 100));
        return Math.round((netUnitPrice + Number.EPSILON) * 100) / 100;
    }

    getBillingAddress(countries: any[], billingAddressInfo: any, states: any, selectedCountryCodeTwo: string): any {
        let countryId = 0;
        if (countries && !billingAddressInfo.countryId) {
            let billingCountry: Country;
            if (billingAddressInfo.countryCode && billingAddressInfo.countryCode.length > 2) {
                billingCountry =
                    countries.find((x: any) => x.isocodeThree.toLowerCase()
                        === billingAddressInfo.countryCode.toLowerCase());
            } else if (billingAddressInfo.countryCode && billingAddressInfo.countryCode.length === 2) {
                billingCountry =
                    countries.find((x: any) => x.isocodeTwo.toLowerCase()
                        === billingAddressInfo.countryCode.toLowerCase());
            }
            if (billingCountry) {
                countryId = billingCountry.countryId;
            }
        }
        const stateObj = this.getStateDetailsByStateIdOrCode(states, null, billingAddressInfo.state);
        const billingAddress: any = {
            addressLine1: billingAddressInfo.addressLine1,
            addressLine2: billingAddressInfo.addressLine2,
            addressLine3: billingAddressInfo.addressLine3,
            addressLine4: billingAddressInfo.addressLine4,
            city: billingAddressInfo.city || null,
            state: billingAddressInfo.stateCode || billingAddressInfo.state,
            stateCode: billingAddressInfo.stateCode || billingAddressInfo.state,
            countryCode: billingAddressInfo.countryCode,
            country: billingAddressInfo.countryCode,
            postalCode: billingAddressInfo.postalCode || billingAddressInfo.zip,
            zip: billingAddressInfo.postalCode || billingAddressInfo.zip,
            countryId: billingAddressInfo.countryId || countryId,
            stateId: billingAddressInfo.stateId || stateObj.id,
            name: billingAddressInfo.shipToName || billingAddressInfo.name || billingAddressInfo.firstName,
            addressType: AddressType.BILLING,
            countryCodeTwo: selectedCountryCodeTwo
        };
        return billingAddress;
    }

    /**
     * Get total points
     */
    calculateTotalPoints(products: any[]): number {
        let _totalCV = 0;
        for (let i = 0; i < products.length; i++) {
            if (!products[i].literature) {
                _totalCV += products[i].commissionableVolume * products[i].quantity;
            }
        }
        return _totalCV;
    }

    getLiteratureItems(products: any[], orderRules: OrderRulesResponse): any[] {
        let literatureItems;
        if (products && products.length) {
            literatureItems = products.filter(item => item.literature);
            if (orderRules && orderRules.itemNumbers && orderRules.itemNumbers.length) {
                const tripackItems = products.filter(item => orderRules.itemNumbers.indexOf(+item.itemNumber) > -1);
                if (tripackItems && tripackItems.length) {
                    literatureItems.push(tripackItems);
                }
            }
        }
        return literatureItems;
    }

    validateSubTotalBasedOnType(products: any[], orderRules: OrderRulesResponse, subTotal?: any,isOverrideMinimumLimit?:boolean): boolean {
        let isThresholdCrossed = true;
        const itemSubTotal = subTotal;
        let orderMinValue = orderRules.minValue;
        let orderMaxValue = orderRules.maxValue;
        if (this.isLiteratureValidation(products, orderRules)) {
            orderMinValue = orderRules.literatureMinValue;
            orderMaxValue = orderRules.literatureMaxValue;
        }
        if (itemSubTotal >= orderMinValue && itemSubTotal <= orderMaxValue) {
            isThresholdCrossed = false;
        }
        let hasMinValueIgnoreProduct = this.checkMinValueProduct(products, orderRules);
        if(itemSubTotal<=orderMinValue ) {
            isOverrideMinimumLimit===true || hasMinValueIgnoreProduct ? isThresholdCrossed=false:isThresholdCrossed=true;
        }
        return isThresholdCrossed;
    }

    checkMinValueProduct(products: any, orderRules: OrderRulesResponse): boolean {
        let setMinValue;
        if (products && orderRules && orderRules.ignoreMinValueItemNumbers && orderRules.ignoreMinValueItemNumbers.length) {
            products.forEach(product => {
                const minValueProduct = orderRules.ignoreMinValueItemNumbers.find(itemNumber => itemNumber == product.itemNumber);
                if (minValueProduct) {
                    setMinValue = true;
                }
            });
        }
        return setMinValue;
    }

    isLiteratureValidation(products: any, orderRules: OrderRulesResponse): boolean {
        const literatureItems = this.getLiteratureItems(products, orderRules);
        const isLiterature = literatureItems && literatureItems.length && products && products.length
            && products.length === literatureItems.length;
        return isLiterature;
    }

    getSignupEmail(fpcCreateResponse: EnrollInfo) {
        const emailContact: any = fpcCreateResponse.personalInfo.memberContacts.filter(
            contact => contact.contactTypeID && contact.contactTypeID === ContactTypeValue.EMAIL);
        return emailContact[0].value || fpcCreateResponse.personalInfo.fboId;
    }

    prepareDocumentCheckActivityLogValue(agreementDocuments: DocumentLibrary[]): string {
        let logValue = '';
        if (agreementDocuments) {
            agreementDocuments.forEach((x) => {
                logValue = logValue + x.displayName + ', ';
            });
        }
        logValue = logValue ? logValue.substring(0, logValue.length - 2) + '.' : logValue;
        return logValue;
    }

    /**
     * @description compare tax
     * @param {Order} orderRequest
     * @returns {boolean}
     */
    compareTax(orderRequest: Order, storeInfo: StoreConfig, appConfig: AppCountryConfig): boolean {
        if (orderRequest && orderRequest.taxMetaData && orderRequest.taxMetaData.orderTaxCharges
            && orderRequest.taxMetaData.orderTaxCharges.length > 0) {
            let totalTaxAmount = 0;
                orderRequest.taxMetaData.orderTaxCharges.forEach(tax => {
                    totalTaxAmount = totalTaxAmount + parseFloat(tax.taxAmount.toFixed(2));
                });
            totalTaxAmount = this._currencyPipe.getFormatedCurrency(totalTaxAmount, storeInfo);
            if (orderRequest.orderTax.toFixed(2) === totalTaxAmount.toFixed(2)) {
                return true;
            } else {
                return false;
            }

        } else {
            return true;
        }
    }

    getLanguageCultureCode(storeLanguages: Language[], orderFormGroup: FormGroup): string {
        const language: any = storeLanguages.filter(lang => lang.cultureName.toLowerCase()
            === orderFormGroup.controls['language'].value.toLowerCase());
        return language && language.length > 0 ? language[0].languageCode : '';
    }

    /**
     * @description
     * frame content to be logged for shipping price change
     * @returns {string}
     */
    getShippingLogInfo(storeInfo: StoreConfig, orderFormGroup: FormGroup, shippingCost: number,
        shippingTax: number, orginalShippingCost: number, isVATRegistered: boolean, calculateTaxExclusivePrice: boolean, appConfig:AppCountryConfig, isFbo: boolean): string {
        let shippingLogInfo: string;
        const shippingTotal = this.getOrderShippingCost(shippingCost, shippingTax, isVATRegistered, calculateTaxExclusivePrice, appConfig, isFbo);
        if (orderFormGroup && orderFormGroup.controls['shippingCostSelector']
            && orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.thirdOption.toString()
            && orderFormGroup.controls['shippingCostPercentage']
            && orderFormGroup.controls['shippingCostPercentage'].value > 0) {
            const discountPercentage = orderFormGroup.controls['shippingCostPercentage'].value;
            shippingLogInfo = discountPercentage > 0 ? 'Shipping Cost was discounted by ' +
                (discountPercentage + '%') + '.' : '';
        } else if (orginalShippingCost !== shippingTotal && appConfig.enableVatItemizationForNonVat) {
            const costDifference = shippingTotal - orginalShippingCost;
            shippingLogInfo = costDifference > 0 ? 'Shipping Cost was increased by ' + this._currencyPipe.
                transform(costDifference, storeInfo, true) +
                '.' : 'Shipping Cost was discounted by ' + this._currencyPipe.
                    transform(-costDifference, storeInfo, true) + '.';
        }
        return shippingLogInfo;
    }

    getOrderShippingCost(shippingCost: number, shippingTax: number, isVATRegistered: boolean, calculateTaxExclusivePrice: boolean,appconfig?:AppCountryConfig,isfbo?:boolean): number {
       if(appconfig && appconfig.enableVatItemizationForNonVat){
           return isfbo?shippingCost:(shippingCost + shippingTax);
       }else{
        return ((isVATRegistered || calculateTaxExclusivePrice || (appconfig && appconfig.displayTaxInclusivePrice)) ? shippingCost : (shippingCost + shippingTax));
       }
    }

    getOrderLineItems(orderObject: any, apConfig?:AppCountryConfig): any[] {
        const lineItems: any[] = [];
        if (orderObject.products && orderObject.products.length > 0) {
            let itemLineNumber = 0;
            let lineItemTax: any;
            orderObject.products.sort((x, y) => {
               return (x.comboBOMEnabled === y.comboBOMEnabled) ? 0 : x.comboBOMEnabled ? 1 : -1 ;
              });
            orderObject.products.forEach((product: any) => {
                if (product) {
                    let unitPrice: number = orderObject.countryTaxInclusive ? product.applicablePrice : product.catalogPrice;
                    let retailPrice: number = orderObject.countryTaxInclusive ? product.retailPrice : product.baseRetailPrice;
                    let wholesalePrice: number = orderObject.countryTaxInclusive ? product.wholesalePrice : product.baseWholesalePrice;
                    if (orderObject.displayPriceConfig) {
                        unitPrice = orderObject.displayPriceConfig.displayApplicablePrice ? product.applicablePrice :
                            (orderObject.displayPriceConfig.displayComputedTaxInclusivePrice ? product.computedTaxInclusivePrice :
                                (orderObject.displayPriceConfig.displayCatalogPrice ? product.catalogPrice : product.applicablePrice));
                        retailPrice = orderObject.displayPriceConfig.displayRetailPrice ? product.retailPrice :
                            orderObject.displayPriceConfig.displayBaseRetailPrice ? product.baseRetailPrice : product.retailPrice;
                        wholesalePrice = orderObject.displayPriceConfig.displayWholesalePrice ? product.wholesalePrice :
                            orderObject.displayPriceConfig.displayBaseWholesalePrice ? product.baseWholesalePrice : product.wholesalePrice;
                    }
                    if (orderObject.orderTaxResponse && orderObject.orderTaxResponse.cart
                        && orderObject.orderTaxResponse.cart.orderLineItems
                        && orderObject.orderTaxResponse.cart.orderLineItems.length > 0) {
                        lineItemTax = orderObject.orderTaxResponse.cart.orderLineItems.find(y => y.itemNumber === product.itemNumber);
                    }
                    const lineItem: any = {
                        discountPercentage: product.discountPercentage,
                        literature: product.literature,
                        orderLineNumber: ++itemLineNumber,
                        productId: product.id,
                        itemNumber: product.itemNumber,
                        productName: product.displayName ? product.displayName : product.name,
                        unitRetailPrice: retailPrice,
                        unitWholeSalePrice: wholesalePrice,
                        unitBaseWholeSalePrice: product.baseWholesalePrice ? product.baseWholesalePrice : 0,
                        unitPaidPrice: unitPrice,
                        taxInclusive: (orderObject.checkoutConfig && orderObject.checkoutConfig.storeTaxExclusivePrice) ?
                            false : orderObject.countryTaxInclusive,
                        unitTaxExclusiveRetailPrice: product.baseRetailPrice,
                        unitTaxExclusivePaidPrice: product.catalogPrice,
                        unitCommissionablePrice: 0,
                        importedMeasurementUnit: product.importedMeasurementUnit,
                        revisionNumber: product.revisionNumber,
                        taxRate: product.taxRate || 0,
                        unitTax: lineItemTax ? lineItemTax.salesTax : 0,
                        unitTaxablePrice: lineItemTax && lineItemTax.taxablePrice ? lineItemTax.taxablePrice : 0,
                        unitQualificationValue: product.commissionableVolume || 0,
                        quantity: product.quantity,
                        freeshipping: false,
                        shippable: product.shipping ? product.shipping.shippingRequired : null,
                        fboEligible: this.getFBOEigibleProduct(product),
                        downloadActivated: product.downloadableProduct,
                        weight: product.shipping ? product.shipping.weight : null,
                        height: product.shipping ? product.shipping.height : null,
                        length: product.shipping ? product.shipping.length : null,
                        width: product.shipping ? product.shipping.width : null,
                        // totalAmount: this._currencyPipe.getFormatedCurrency(x.applicablePrice * x.quantity),
                        totalAmount: this._currencyPipe.getFormatedCurrency(
                            this.calculateSubTotal(orderObject.storeInfo, orderObject.countryTaxInclusive, product,
                                orderObject.checkoutConfig, apConfig), orderObject.storeInfo),
                        discountAmount: this._currencyPipe.getFormatedCurrency
                            (this.calculateDiscountAmount(product, orderObject.displayPriceConfig), orderObject.storeInfo),
                        priceTierName: product.priceTierName,
                        comboBOMEnabledProduct: product.display ? (product.display.comboBOMEnabled ? true : false) : false,
                        depositFeeProduct: product.display ? (product.display.depositFeeProduct? true: false) : false,
                        shippableFulfillment: product.shipping ? (product.shipping.shippableFulfillment? true: false) : false,
                        depositFeeEnabledProduct: product.productMetaData ? (product.productMetaData.find(x => x.metaTitle === ProductMetatDataValue.DEPOSIT_FEE_ENABLED && x.metaKeyWords === '1')? true: false) : false,
                        orderPromotionTrackings: (product.orderPromotionTrackings && product.orderPromotionTrackings.length > 0) ? product.orderPromotionTrackings : []
                    };
                    lineItems.push(lineItem);
                }
            });
        }
        return lineItems;
    }

    getFBOEigibleProduct(product: Product): boolean {
        if (product.productMetaData) {
            const fboEligibilty = product.productMetaData.
                find(x => x.metaTitle === ProductMetatDataValue.DISTRIBUTORELIGIBLE);
            if (fboEligibilty && fboEligibilty.metaKeyWords === '1') {
                return true;
            }
        }
    }

    getOrderShippings(shippingObject: any,appConfig:AppCountryConfig, isFbo: boolean): any[] {
        const orderShippings: any[] = [];
        let shippingAddressId = null;
        if (shippingObject.savedAddressId) {
            shippingAddressId = shippingObject.savedAddressId;
        } else if (shippingObject.verifiedShippingAddressId) {
            shippingAddressId = shippingObject.verifiedShippingAddressId;
        }
        const discountPercentage = shippingObject.orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.thirdOption.toString()
            && shippingObject.orderFormGroup.controls['shippingCostPercentage'].value > 0
            ? shippingObject.orderFormGroup.controls['shippingCostPercentage'].value : 0;
        const orderShipping: any = {
            deliveryPreferenceId: shippingObject.orderFormGroup.value.deliveryPreference,
            shipMethodId: shippingObject.orderFormGroup.value.shipMethodId,
            shippingAddressId: (shippingObject.orderFormGroup.value.deliveryPreference === DeliveryPreference.DELIVERY) ?
            shippingObject.shippingAddressId : null,
            shortCode: shippingObject.shipMethod.shortCode,
            preferredTime: null,
            notes: null,
            shipmentDate: null,
            shippingWaived: shippingObject.originalShippingCost > 0 ? (shippingObject.memberShippingDiscountOfferEligible ? true :
                 ((+shippingObject.originalShippingCost) - (+this.getOrderShippingCost(shippingObject.shippingCost,
                    shippingObject.shippingTax, shippingObject.isVATRegistered,
                    shippingObject.calculateTaxExclusivePrice,appConfig,isFbo))) === 0 ? false : true) :false,
            shippingWaiverPercentage: (discountPercentage != null && discountPercentage > 0) ? discountPercentage : null,
            distributionCenter: shippingObject.distributionCenterName
        };
        if(shippingObject.orderPromotionTrackingList && shippingObject.orderPromotionTrackingList.length){
            orderShipping.orderPromotionTrackingList = shippingObject.orderPromotionTrackingList;
        }

        if (orderShipping.deliveryPreferenceId === ShippingType.PICKUP) {
            orderShipping.distributionCenter = (shippingObject.shipMethod.pickUpLocation.pickupCenterName) ?
            shippingObject.shipMethod.pickUpLocation.pickupCenterName : '';
        }
        orderShippings.push(orderShipping);
        return orderShippings;
    }

    getPaymentMethodById(paymentMethodId: number): any {
        let paymentType: string;
        switch (paymentMethodId) {
            case PaymentType.Credit_Card:
                paymentType = PaymentMethodTypes.Credit_Card;
                break;
            case PaymentType.eCheck:
                paymentType = PaymentMethodTypes.eCheck;
                break;
            case PaymentType.Money_order:
                paymentType = PaymentMethodTypes.Money_order;
                break;
            case PaymentType.Cash:
                paymentType = PaymentMethodTypes.Cash;
                break;
            case PaymentType.Orange_Money:
                paymentType = PaymentMethodTypes.Orange_Money;
                break;
            case PaymentType.Ideal:
                paymentType = PaymentMethodTypes.Ideal;
                break;
            case PaymentType.PayPal:
                paymentType = PaymentMethodTypes.PayPal;
                break;
            case PaymentType.Cash_On_Delivery:
                paymentType = PaymentMethodTypes.Cash_On_Delivery;
                break;
            case PaymentType.Bank_Transfer:
                paymentType = PaymentMethodTypes.Bank_Transfer;
                break;
            case PaymentType.BanContact_MisterCash_and_Maestro:
                paymentType = PaymentMethodTypes.BanContact_MisterCash_and_Maestro;
                break;
            case PaymentType.MisterCash_and_Maestro:
                paymentType = PaymentMethodTypes.MisterCash_and_Maestro;
                break;
            case PaymentType.Pay_By_Link:
                paymentType =PaymentMethodTypes.Pay_By_Link;
                break;
            case PaymentType.Wire_Transfer:
                paymentType = PaymentMethodTypes.Wire_Transfer;
                break;
            case PaymentType.Walley:
                paymentType = PaymentMethodTypes.Walley;
                break;
            case PaymentType.Riverty:
                paymentType = PaymentMethodTypes.Riverty;
                break;
            default:
                paymentType = PaymentMethodTypes.Credit_Card;
                break;

        }
        return paymentType;
    }

    calculateOrderTotal(orderObject: any,isCalculateTax?:boolean): number {
        const isCalculateTaxExclusivePrice = orderObject.checkoutConfig && orderObject.checkoutConfig.calculateTaxExclusivePrice;
        let totalCost = 0;
        let itemsTotalTax = 0;
        let calculateVatTax=true;
        for (let i = 0; i < orderObject.products.length; i++) {
            if (!orderObject.countryTaxInclusive) {
                itemsTotalTax += orderObject.products[i].tax;
            }
        }
        if (orderObject.isVATRegistered && orderObject.countryTaxInclusive) {
            if(orderObject.appConfig && orderObject.appConfig.enableVatItemizationForNonVat){
                calculateVatTax =false;
            }
            totalCost = this.calculateItemsSubTotal(orderObject.storeInfo, orderObject.countryTaxInclusive, orderObject.products,
                orderObject.checkoutConfig, orderObject.isVATRegistered, orderObject.isEnableVATCalculation,orderObject.appConfig,isCalculateTax) +
                (orderObject.importFeeValue) + (orderObject.isShippingCostWaived ? 0 :
                    this.getShippingCostForOrderSummary(orderObject.shippingCost, orderObject.shippingTax, orderObject.orderFormGroup,
                    orderObject.isShippingCostWaived, orderObject.isVATRegistered, isCalculateTaxExclusivePrice) || 0)
                + (calculateVatTax ? orderObject.taxableAmount|| 0 :0);
        } else {
            if(orderObject.appConfig && orderObject.appConfig.enableVatItemizationForNonVat) {
                calculateVatTax = (orderObject.memberInfo && orderObject.memberInfo.vatRegistered)?false:true;
            }
            totalCost = this.calculateItemsSubTotal(orderObject.storeInfo, orderObject.countryTaxInclusive, orderObject.products,
                orderObject.checkoutConfig, orderObject.isVATRegistered, orderObject.isEnableVATCalculation,orderObject.appConfig,isCalculateTax) +
                (orderObject.importFeeValue) + (orderObject.isShippingCostWaived ? 0 :
                    this.getShippingCostForOrderSummary(orderObject.shippingCost,
                    orderObject.shippingTax, orderObject.orderFormGroup,
                    orderObject.isShippingCostWaived, orderObject.isVATRegistered,
                        (orderObject.checkoutConfig ? orderObject.checkoutConfig.calculateTaxExclusivePrice : false),orderObject.appConfig,orderObject.isFbo?orderObject.isFbo:false) || 0) +
                ((orderObject.countryTaxInclusive === false || isCalculateTaxExclusivePrice) ?
                (calculateVatTax ?orderObject.taxableAmount || 0: 0) : 0);
        }
        if (orderObject.storeInfo) {
            // tslint:disable-next-line:no-bitwise
            totalCost = Number(totalCost.toFixed(Math.max(0, ~~orderObject.storeInfo.numberDecimals)));
        }
        return totalCost;
    }

    getShippingCostForOrderSummary(shippingCost: number, shippingTax: number, orderFormGroup: FormGroup,
        isShippingCostWaived: boolean, isVATRegistered: boolean, calculateTaxExclusivePrice: boolean,appconfig?:AppCountryConfig,isFbo?:boolean): number {
        let shippingTotal = +this.getOrderShippingCost(shippingCost, shippingTax, isVATRegistered, calculateTaxExclusivePrice,appconfig,isFbo) || 0;
        if (isShippingCostWaived) {
            shippingTotal = 0;
        } else if (orderFormGroup.value.isShippingDiscountApplied) {
            if ((orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.secondOption.toString()
                && shippingCost > 0)
                || (orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.thirdOption.toString()
                    && orderFormGroup.controls['shippingCostPercentage'].value > 0)) {
                const skipShippingTaxCalculation = (appconfig && appconfig.enableVatItemizationForNonVat && isFbo) || (appconfig && appconfig.skipShippingTaxCalculationInShipping) ;
                const shippingCostWithTax = (shippingCost || 0) + (shippingTax || 0);
                shippingTotal = skipShippingTaxCalculation ? shippingCost : shippingCostWithTax;
            } else if ((orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.secondOption.toString()
                && shippingCost === 0)
                || (orderFormGroup.controls['shippingCostSelector'].value === SelectEnum.thirdOption.toString()
                    && orderFormGroup.controls['shippingCostPercentage'].value === 100)) {
                shippingTotal = 0;
            }
        }
        return shippingTotal;
    }

    getAddresses(addressObject: any,foreverDirectEnabled?:boolean): any {
        const addresses: any[] = [];
        if (addressObject.orderFormGroup.value.deliveryPreference === DeliveryPreference.PICKUP) {
            addresses.push(this.getPickUpAddress(addressObject.shipMethod, addressObject.orderFormGroup.value.deliveryPreference, addressObject.countries,
                addressObject.states, addressObject.countryCode));
        } else {
            addresses.push(this.getShippingAddress(addressObject.shippingAddressInfo,addressObject.pudoShippingAddressInfo,addressObject.selectedCountryCodeTwo?addressObject.selectedCountryCodeTwo:null,
                addressObject.orderFormGroup.value, addressObject.countries,foreverDirectEnabled));
        }
        if (addressObject.orderFormGroup.value.deliveryPreference === DeliveryPreference.DROPSHIP) {
            const pudoPickUpAddress = this.getPudoPickUpAddress(addressObject);
            pudoPickUpAddress.deliveryInstruction = pudoPickUpAddress && pudoPickUpAddress.instruction ? pudoPickUpAddress.instruction : '';
            pudoPickUpAddress.email = pudoPickUpAddress.email ? pudoPickUpAddress.email : null;
            addresses.push(pudoPickUpAddress);
            
        }
        if ((addressObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.firstOption.toString()) || (addressObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.secondOption.toString())) {
            addresses.push(this.getBillingAddress(addressObject.countries, addressObject.billingAddressInfo, addressObject.states,
                addressObject.selectedCountryCodeTwo));
        }
        return addresses;
    }

    getPudoPickUpAddress(addressObject:any): any {
        if (
            addressObject.orderFormGroup.value.deliveryPreference === DeliveryPreference.DROPSHIP) {

            const pudoCollectionPoint = addressObject.selectedpudoAddress?addressObject.selectedpudoAddress :addressObject.pudoShippingAddressInfo;
            const countryObj = this.getCountryDetailsByCountryIdOrCode(addressObject.countries,null , addressObject.countryCode);
            const stateObj = this.getStateDetailsByStateIdOrCode(addressObject.states, null,addressObject.pudoShippingAddressInfo.stateCode);
            const pudoPickUpAddress: any = {
                addressLine1: pudoCollectionPoint.street ? (pudoCollectionPoint.street + ' ' + pudoCollectionPoint.houseNumber):pudoCollectionPoint.addressLine1,
                addressLine2: pudoCollectionPoint.address2 ? pudoCollectionPoint.address2 : pudoCollectionPoint.addressLine2,
                addressLine3: pudoCollectionPoint.address3 ? pudoCollectionPoint.address3 : pudoCollectionPoint.addressLine3,
                addressLine4: null,
                city: pudoCollectionPoint.city,
                state: stateObj.state ,
                stateCode: stateObj.stateCode ? stateObj.stateCode :'' ,
                countryCode: countryObj && countryObj.isocodeThree ? countryObj.isocodeThree : '',
                country: countryObj && countryObj.country ? countryObj.country :'',
                county: null,
                zip: pudoCollectionPoint.zipCode ? pudoCollectionPoint.zipCode : '',
                company: null,
                name: pudoCollectionPoint.name,
                pickupCenterName: pudoCollectionPoint.name,
                instruction: '',
                optionName: DelivaryOptionType.Dropship,
                pickUpCenterId: addressObject.shippingMethod && addressObject.shippingMethod.pickUpLocation &&
                addressObject.shippingMethod.pickUpLocation.pickUpCenterId ? addressObject.shippingMethod.pickUpLocation.pickUpCenterId : null,
                latitude: addressObject.selectedpudoAddress ? pudoCollectionPoint.geoPoint.latitude + '' :null,
                longitude: addressObject.selectedpudoAddress ? pudoCollectionPoint.geoPoint.longitude + '' : null,
                countryId: countryObj.countryId,
                stateId: stateObj.id,
                businessInformation: [],
                residential: false,
                validAddress: false,
                addressType: AddressType.PUDO,
                postalCode: pudoCollectionPoint.zipCode,
                pudoNetwork: pudoCollectionPoint.network,
                pudoExternalId: pudoCollectionPoint.externalId,
                pudoIdentifier: pudoCollectionPoint.identifier
            };
            return pudoPickUpAddress;
        }
    }

    addNewProductItem(items: any, index: number, product: any, uiTemplateBaseUrl: string, cdnURL: string, disableUpdate?: boolean) {
        const itemValue = product.itemNumber + ' - ' + product.name;
        items.controls[index].controls['itemNumber'].setValue(itemValue);
        const itemImage = (product.imageModelList && product.imageModelList.length > 0)
            ? (product.imageModelList[0].details.find((imageDetail: any) => imageDetail.type === ImageType.Thumbnail)) : '';
        const imageUrl = (itemImage && itemImage.url) ? (cdnURL + '/' + itemImage.url)
            : (uiTemplateBaseUrl + '/images/no_image.png');
        const isDepositFeeProduct = product.productMetaData.find(x => x.metaTitle === ProductMetatDataValue.DEPOSIT_FEE_PRODUCT && x.metaKeyWords === '1');
        const depositFeeEnabled = product.productMetaData.find(x => x.metaTitle === ProductMetatDataValue.DEPOSIT_FEE_ENABLED && x.metaKeyWords === '1');
        items.controls[index].controls['productImage'].setValue(imageUrl);
        items.controls[index].controls['productNumber'].setValue(product.itemNumber);
        items.controls[index].controls['productName'].setValue(product.displayName ? product.displayName : product.name);
        items.controls[index].controls['productId'].setValue(product.id);
        product.promoProduct = false;
        items.controls[index].controls['productItem'].setValue(product);
        if (disableUpdate) {
            items.controls[index].controls['disableIncrement'].setValue(true);
            items.controls[index].controls['disableDelete'].setValue(true);
        } else {
            items.controls[index].controls['disableIncrement'].setValue(false);
            items.controls[index].controls['disableDelete'].setValue(false);
        }
        items.controls[index].controls['disableDecrement'].setValue(true);
        items.controls[index].controls['isDepositFeeProduct'].setValue(!!isDepositFeeProduct);
        items.controls[index].controls['depositFeeEnabled'].setValue(!!depositFeeEnabled);
        return items;
    }

    stockAvailable(product: Product): boolean {
        return (!(!product.stockAvailable || (product.manageStock && product.stockQuantity <=0 )));
    }

    getOrderPaymentInfo(paymentObject: any): any[] {
        const payments: any[] = [];
        let paymentMethodType: string;

        paymentMethodType = this.getPaymentMethodById(+paymentObject.orderFormGroup.controls['paymentSelector'].value);

        const orderPayment: any = {
            savePaymentMethod: paymentObject.savePaymentMethod ? paymentObject.savePaymentMethod : false,
            paymentAmount: this.calculateOrderTotal(paymentObject),
            priceTier : this.getStorePriceTierName(paymentObject),
            redirect: false,
            orderSourceTypeId: OrderSourceValue.CUSTOMER,
            memberPaymentMethodId: +paymentObject.orderFormGroup.controls['paymentSelector'].value,
            authorizationCode:paymentObject.authorizationCode ? paymentObject.authorizationCode : null,
            creditCardModel: {
                currencyCode: paymentObject.storeInfo.currencyCode,
                paymentToken: paymentObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.firstOption.toString()
                    ? paymentObject.selectedPayvisionCardInfo.token : null,
                paymentMethodType: paymentMethodType,
                maskedCreditCardNumber: paymentObject.orderFormGroup.controls['paymentSelector'].value
                    === SelectEnum.firstOption.toString() ? paymentObject.selectedPayvisionCardInfo.maskedCard : null,
                cardHolderName: paymentObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.firstOption.toString()
                    ? paymentObject.selectedPayvisionCardInfo.nameOnTheCard : null,
                expMonth: paymentObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.firstOption.toString()
                    ? paymentObject.selectedPayvisionCardInfo.expiryMonth : null,
                expYear: paymentObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.firstOption.toString()
                    ? paymentObject.selectedPayvisionCardInfo.expiryYear : null
            },
            paymentECheck: (paymentObject.orderFormGroup.controls['paymentSelector'].value === SelectEnum.secondOption.toString()
            ) ? this.getEcheckInfo(paymentObject.echeckRequest, paymentObject.eCheckTokenRes) : null
        };
        payments.push(orderPayment);
        return payments;
    }

    getEcheckInfo(echeckRequest: any, eCheckTokenRes) {
        const eCheckInfo = {
            bankName: echeckRequest.bankName,
            accountNumber: echeckRequest.accountNumber,
            routingNumber: echeckRequest.routingNumber,
            accountType: echeckRequest.accountType,
            mecTransactionID: '',
            nameOnBankAccount: echeckRequest.nameOnBankAccount,
            customMessage: '',
            reusable: echeckRequest.reusable,
            eCheckID: eCheckTokenRes ? parseInt(atob(eCheckTokenRes), 10) : echeckRequest ? echeckRequest.id : 0,
            email: echeckRequest.email,
            phone: echeckRequest.phone
        };
        return eCheckInfo;
    }

    /**
     * @description prepare Terms and Conditions Document request
     * prepareAgreementDocumentsRequest
     */
    prepareAgreementDocumentsRequest(orderType: OrderType, countryCode: string, language: string): DocumentLibraryRequest {
        return {
            customerTypeId: orderType === OrderType.NEW_FBO ? DocumentLibraryMemberId.Distributor : DocumentLibraryMemberId.Novus,
            portalId: Portal.Csp,
            displaySectionId: orderType === OrderType.NEW_FBO ? DisplaySectionId.FBO_Checkout_Page : DisplaySectionId.FPC_Checkout_Page,
            countryCode: countryCode,
            languageCode: language
        };
    }

    getItemVolumeSum(items): number {
        if (this.appConfig.roundoffTaxAmount) {
            return Math.round(items
                .map(function (x) { return x.taxAmount; })
                .reduce(function (a, b) { return a + b; }));
        } else {
            return items
                .map(function (x) { return x.taxAmount; })
                .reduce(function (a, b) { return a + b; });
        }
    }

    prepareVerifyAccountRequest(personalInfo: any, address: any): VerifyAccountModel {
        const verifyAccountModel: VerifyAccountModel = {
            includeMemberTypeToVerify: true,
            firstName: personalInfo.firstName,
            lastName: personalInfo.lastName,
            memberTypeId: null,
            addressTypeId: AddressType.MAILING,
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2 ? address.addressLine2 : '',
            city: address.city,
            stateId: address.stateId,
            postalCode: address.postalCode,
            countryId: address.countryId
        };
        return verifyAccountModel;
    }

    mapBillingAddressService(billingAddressInfo, stateObj){
        const billingMappedAddress = {
            id: null,
            addressLine1: billingAddressInfo.addressLine1,
            addressLine2: billingAddressInfo.addressLine2 || '',
            addressLine3: billingAddressInfo.addressLine3 || '',
            addressLine4: billingAddressInfo.addressLine4 || '',
            stateId: billingAddressInfo.stateId || stateObj.id,
            countryId: billingAddressInfo.countryId,
            city: billingAddressInfo.city,
            state: billingAddressInfo.stateCode || billingAddressInfo.state,
            stateCode: billingAddressInfo.stateCode || billingAddressInfo.state,
            country: billingAddressInfo.countryCode,
            countryCode: billingAddressInfo.countryCode,
            postalCode: billingAddressInfo.postalCode,
            name: billingAddressInfo.shipToName || billingAddressInfo.name || billingAddressInfo.firstName,
            firstName: billingAddressInfo.shipToName || billingAddressInfo.name,
            phone: ''
        };
        return billingMappedAddress;
    }
    getStorePriceTierName(orderPaymentObj: any) {
        if (orderPaymentObj && orderPaymentObj.products.length > 0) {
           this.productDiscountPercentage = orderPaymentObj.products[0].storePriceTierPercentage  ?  orderPaymentObj.products[0].storePriceTierPercentage :0;
            const priceTierId: any = StorerPiceTier[orderPaymentObj.products[0].priceTierName];
            const enableWHLSAL00ForAsstSupervisor = this._commonService.isWHLSAL00enabledForAsstSupervisor(orderPaymentObj.checkoutConfig.countryCode);
                if(priceTierId){
                    this.priceTierName = this._commonService.getDiscountPriceLevel(priceTierId, +this.productDiscountPercentage, orderPaymentObj.memberTypeId, enableWHLSAL00ForAsstSupervisor);
                    return this.priceTierName;
                }
    
        }
       
    }

    getSessionGuid() : any{
        const uuid = SSO.getnonce();
        return  uuid;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/enrollment-common.service.ts